import { AlertTriangle, Info } from 'lucide-react'
import type { ModuleId } from '@/constants/modules'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/utils/cn'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
  onConfirm: () => void
  loading?: boolean
  /** Retained for call-site compatibility — no longer drives rainbow chrome. */
  moduleId?: ModuleId
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  const isDestructive = variant === 'destructive'

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        size="sm"
        className={cn(
          'border',
          isDestructive ? 'border-red-500/25' : 'border-border',
        )}
      >
        <AlertDialogHeader
          className={cn(
            'border-b',
            isDestructive
              ? 'border-red-500/20 bg-red-500/[0.04]'
              : 'border-border/60 bg-muted/30',
          )}
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-10 sm:w-10',
                isDestructive
                  ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isDestructive ? (
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.75} />
              ) : (
                <Info className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.75} />
              )}
            </div>
            <AlertDialogTitle className="min-w-0 flex-1 text-left text-base font-semibold leading-snug tracking-tight sm:text-[1.05rem]">
              {title}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="mt-3 text-left text-[13px] leading-relaxed text-muted-foreground sm:mt-3.5 sm:text-sm">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2.5">
          <AlertDialogCancel
            disabled={loading}
            className="h-10 w-full rounded-xl sm:h-9 sm:w-auto"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            variant={isDestructive ? 'destructive' : 'default'}
            className="h-10 w-full rounded-xl sm:h-9 sm:w-auto"
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
